import { Building2, Check, ChevronsUpDown, Loader2, Search } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface OrganisationOption {
  id?: number | string
  value?: number | string
  name: string
}

interface OrganisationSearchInputProps {
  value?: string
  onValueChange: (organisationId: string) => void
  label?: string
  placeholder?: string
  id?: string
  required?: boolean
  disabled?: boolean
  isLoading?: boolean
  options: OrganisationOption[]
  className?: string
}

export function OrganisationSearchInput({
  value,
  onValueChange,
  label = 'Organisation',
  placeholder = 'Select organisation...',
  id,
  required = false,
  disabled = false,
  isLoading = false,
  options,
  className,
}: OrganisationSearchInputProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const normalizedOptions = useMemo(() => (
    options
      .map(option => ({
        id: option.id ?? option.value,
        name: option.name,
      }))
      .filter((option): option is { id: string | number; name: string } => option.id !== undefined)
  ), [options])

  const filteredOptions = useMemo(() => {
    const search = searchQuery.trim().toLowerCase()
    if (!search) {
      return normalizedOptions
    }
    return normalizedOptions.filter(option => (
      option.name.toLowerCase().includes(search)
      || String(option.id).toLowerCase().includes(search)
    ))
  }, [normalizedOptions, searchQuery])

  const selectedOption = useMemo(() => (
    normalizedOptions.find(option => String(option.id) === value)
  ), [normalizedOptions, value])

  const handleSelect = useCallback((optionId: string | number) => {
    onValueChange(String(optionId))
    setOpen(false)
  }, [onValueChange])

  const displayText = selectedOption ? selectedOption.name : placeholder
  const emptyMessage = isLoading
    ? 'Loading organisations...'
    : normalizedOptions.length === 0
      ? 'No organisations available'
      : 'No organisations found'

  return (
    <div className={cn('space-y-2', className)}>
      {label
        ? (
          <Label htmlFor={id}>
            {label}
            {required ? <span className="text-destructive ml-1">*</span> : null}
          </Label>
        )
        : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className={cn('truncate', !selectedOption && 'text-muted-foreground')}>
              {displayText}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
          <Command shouldFilter={false} className="rounded-lg border-0">
            <div className="relative border-b mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/60" />
              <CommandInput
                placeholder="Type to search organisations..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                className="pl-2 border-0 mb-4 focus:ring-0"
              />
              {isLoading
                ? (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )
                : null}
            </div>
            <CommandList className="max-h-[280px]">
              <CommandEmpty>
                <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
                  {isLoading
                    ? (
                      <>
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
                      </>
                    )
                    : (
                      <>
                        <Building2 className="h-8 w-8 text-muted-foreground/50" />
                        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
                      </>
                    )}
                </div>
              </CommandEmpty>
              {filteredOptions.length > 0
                ? (
                  <CommandGroup heading={`${filteredOptions.length} organisation${filteredOptions.length !== 1 ? 's' : ''}`}>
                    {filteredOptions.map(option => (
                      <CommandItem
                        key={option.id}
                        value={String(option.id)}
                        onSelect={() => handleSelect(option.id)}
                        className="flex items-center gap-3 py-3"
                      >
                        <Check
                          className={cn(
                            'h-4 w-4 shrink-0',
                            value === String(option.id) ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                        <div className="flex flex-col overflow-hidden">
                          <span className="truncate text-sm font-medium">{option.name}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )
                : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
